// app/src/lib/cashflow/payments.js — per-vendor payment policy + invoice paid/unpaid.
//
// Whether an invoice is "paid" is derived from the vendor's payment policy, with
// a per-invoice manual override on top:
//   - card               → paid on the receipt date (credit-card purchases)
//   - check_on_delivery  → paid on the delivery (issued) date
//   - statement          → semi-monthly billing (periods 1–15 and 16–end);
//                          the period's invoices are paid N days after the period
//                          closes, so they read "unpaid" until that date.
//   - terms / unset      → outstanding until paid manually (no auto rule)
//
// Pure helpers (statementDates / invoicePayment) take an explicit `today` so they
// are deterministic and testable.

import { db } from '../db.js';
import { vendorLabel } from '../demo.js';
import { requireUserId } from '../procurement/util.js';

/** Human labels for the payment methods (UI selectors). Monthly is the longest
 * cycle used in the food trade — there is nothing beyond it. */
export const PAYMENT_METHODS = {
  card: 'Credit card',
  check_on_delivery: 'Check on delivery',
  statement: 'Statement (semi-monthly)',
  monthly: 'Monthly statement',
  terms: 'On account / net terms',
};

/** Monthly statement close (month-end) + expected payment date. */
export function monthlyDates(issuedIso, offsetDays = DEFAULT_STATEMENT_OFFSET_DAYS) {
  const closeIso = lastDayOfMonth(issuedIso);
  return { closeIso, expectedPayIso: addDays(closeIso, offsetDays) };
}

const DEFAULT_STATEMENT_OFFSET_DAYS = 5;

function isoToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function lastDayOfMonth(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}

/**
 * Semi-monthly statement close + expected payment date for an invoice issued on
 * `issuedIso`. Period 1–15 closes on the 15th; 16–end closes on the last day of
 * the month. Payment lands `offsetDays` after the close.
 * @param {string} issuedIso  YYYY-MM-DD
 * @param {number} [offsetDays]
 * @returns {{ closeIso:string, expectedPayIso:string }}
 */
export function statementDates(issuedIso, offsetDays = DEFAULT_STATEMENT_OFFSET_DAYS) {
  const day = Number(issuedIso.slice(8, 10));
  const closeIso = day <= 15 ? `${issuedIso.slice(0, 8)}15` : lastDayOfMonth(issuedIso);
  return { closeIso, expectedPayIso: addDays(closeIso, offsetDays) };
}

/**
 * Resolve paid/unpaid for one invoice given its vendor's policy.
 * @param {import('../db.js').Invoice} invoice
 * @param {import('../db.js').Vendor=} vendor
 * @param {string} [today]
 * @returns {{ paid:boolean, paidDate:string|null, expectedPayIso:string|null, basis:string }}
 */
export function invoicePayment(invoice, vendor, today = isoToday()) {
  const issued = invoice.issuedDate || today;

  // Manual override always wins.
  if (invoice.paidOverride === 'paid') {
    return { paid: true, paidDate: invoice.paidDate || issued, expectedPayIso: null, basis: 'manual' };
  }
  if (invoice.paidOverride === 'unpaid') {
    const off = vendor?.statementPayOffsetDays ?? DEFAULT_STATEMENT_OFFSET_DAYS;
    const exp = vendor?.paymentMethod === 'statement' ? statementDates(issued, off).expectedPayIso
      : vendor?.paymentMethod === 'monthly' ? monthlyDates(issued, off).expectedPayIso
      : (invoice.dueDate || null);
    return { paid: false, paidDate: null, expectedPayIso: exp, basis: 'manual' };
  }

  const method = vendor?.paymentMethod;
  if (method === 'card' || method === 'check_on_delivery') {
    return { paid: true, paidDate: issued, expectedPayIso: null, basis: method };
  }
  if (method === 'statement' || method === 'monthly') {
    const off = vendor.statementPayOffsetDays ?? DEFAULT_STATEMENT_OFFSET_DAYS;
    const { expectedPayIso } = method === 'monthly' ? monthlyDates(issued, off) : statementDates(issued, off);
    const paid = today >= expectedPayIso;
    return { paid, paidDate: paid ? expectedPayIso : null, expectedPayIso, basis: method };
  }
  // 'terms' or no policy set → outstanding until manually paid.
  return { paid: false, paidDate: null, expectedPayIso: invoice.dueDate || null, basis: method || 'unset' };
}

/**
 * Outstanding (unpaid) invoices, grouped by vendor with the soonest expected
 * payment date. Confirmed duplicates are excluded.
 * @param {string} [today]
 */
export async function unpaidSummary(today = isoToday()) {
  const userId = await requireUserId();
  const invoices = (await db.invoices.where('userId').equals(userId).toArray())
    .filter((i) => !i.isDuplicate);
  const vendorIds = Array.from(new Set(invoices.map((i) => i.vendorId).filter(Boolean)));
  const vendors = vendorIds.length ? await db.vendors.where('vendorId').anyOf(vendorIds).toArray() : [];
  const vmap = new Map(vendors.map((v) => [v.vendorId, v]));

  const byVendor = new Map();
  let totalCents = 0, count = 0;
  for (const inv of invoices) {
    const v = vmap.get(inv.vendorId);
    const p = invoicePayment(inv, v, today);
    if (p.paid) continue;
    totalCents += inv.totalCents || 0; count++;
    const key = inv.vendorId || 'unknown';
    const cur = byVendor.get(key) || {
      vendorId: key, vendorName: vendorLabel(v?.name),
      method: v?.paymentMethod || null, unsetPolicy: !v?.paymentMethod,
      totalCents: 0, count: 0, nextPayIso: null,
    };
    cur.totalCents += inv.totalCents || 0; cur.count++;
    if (p.expectedPayIso && (!cur.nextPayIso || p.expectedPayIso < cur.nextPayIso)) cur.nextPayIso = p.expectedPayIso;
    byVendor.set(key, cur);
  }
  const vendorsOut = Array.from(byVendor.values())
    .sort((a, b) => (a.nextPayIso || '9999-99-99') < (b.nextPayIso || '9999-99-99') ? -1 : 1);
  return { totalCents, count, vendors: vendorsOut };
}

/**
 * The PREVIOUS statement period actually paid, per deferred-payment vendor
 * (statement / monthly). For each such vendor: group its invoices by statement
 * close date, keep only periods whose expected pay date has passed (the money
 * left the bank), and report the MOST RECENT one — "what we paid City Produce
 * for the last period". Pairs with unpaidSummary (what we still owe them for
 * the open period).
 *
 * Each reported period carries the counted `invoices` AND an `excluded` list —
 * same-period invoices dropped from the paid total (confirmed duplicates or
 * manually-flagged unpaid) with the reason — so the receipts drill-in can show
 * exactly why the total is what it is when it doesn't match a vendor statement.
 * @param {string} [today]
 * @returns {Promise<{ totalCents:number, count:number, vendors:Array<{
 *   vendorId:string, vendorName:string, method:string, closeIso:string,
 *   paidIso:string, totalCents:number, count:number,
 *   invoices:Array<{ invoiceId:string, invoiceNumber:string|null, issuedDate:string, totalCents:number }>,
 *   excludedCents:number,
 *   excluded:Array<{ invoiceId:string, invoiceNumber:string|null, issuedDate:string, totalCents:number, reason:'duplicate'|'unpaid' }>,
 * }> }>}
 */
export async function lastPeriodPaidSummary(today = isoToday()) {
  const userId = await requireUserId();
  const invoices = await db.invoices.where('userId').equals(userId).toArray();
  const vendorIds = Array.from(new Set(invoices.map((i) => i.vendorId).filter(Boolean)));
  const vendors = vendorIds.length ? await db.vendors.where('vendorId').anyOf(vendorIds).toArray() : [];
  const vmap = new Map(vendors.map((v) => [v.vendorId, v]));

  // vendorId → closeIso → { paidIso, totalCents, count, invoices[], excludedCents, excluded[] }
  const periods = new Map();
  for (const inv of invoices) {
    const v = vmap.get(inv.vendorId);
    const method = v?.paymentMethod;
    if (method !== 'statement' && method !== 'monthly') continue;
    const issued = inv.issuedDate; if (!issued) continue;
    const off = v.statementPayOffsetDays ?? undefined;
    const { closeIso, expectedPayIso } = method === 'monthly'
      ? monthlyDates(issued, off) : statementDates(issued, off);
    if (expectedPayIso > today) continue; // period not paid yet — it's the OPEN balance

    let byClose = periods.get(inv.vendorId);
    if (!byClose) { byClose = new Map(); periods.set(inv.vendorId, byClose); }
    const cur = byClose.get(closeIso) || { paidIso: expectedPayIso, totalCents: 0, count: 0, invoices: [], excludedCents: 0, excluded: [] };
    const detail = { invoiceId: inv.invoiceId, invoiceNumber: inv.invoiceNumber || null, issuedDate: issued, totalCents: inv.totalCents || 0 };
    // A duplicate or a manual-unpaid override is in the period by date but not in
    // the paid total — surface it so a mismatch vs the vendor statement is visible.
    const reason = inv.isDuplicate ? 'duplicate' : inv.paidOverride === 'unpaid' ? 'unpaid' : null;
    if (reason) { cur.excluded.push({ ...detail, reason }); cur.excludedCents += detail.totalCents; }
    else { cur.invoices.push(detail); cur.totalCents += detail.totalCents; cur.count += 1; }
    byClose.set(closeIso, cur);
  }

  let totalCents = 0, count = 0;
  const vendorsOut = [];
  for (const [vendorId, byClose] of periods) {
    // The latest close period that actually PAID something (a period with only
    // excluded invoices paid $0 and isn't the "last payment").
    const latestClose = [...byClose.entries()].filter(([, p]) => p.count > 0).map(([k]) => k).sort().pop();
    if (!latestClose) continue;
    const p = byClose.get(latestClose);
    const v = vmap.get(vendorId);
    p.invoices.sort((a, b) => (a.issuedDate < b.issuedDate ? -1 : 1));
    p.excluded.sort((a, b) => (a.issuedDate < b.issuedDate ? -1 : 1));
    vendorsOut.push({
      vendorId, vendorName: vendorLabel(v?.name), method: v?.paymentMethod || null,
      closeIso: latestClose, paidIso: p.paidIso,
      totalCents: p.totalCents, count: p.count, invoices: p.invoices,
      excludedCents: p.excludedCents, excluded: p.excluded,
    });
    totalCents += p.totalCents; count += p.count;
  }
  vendorsOut.sort((a, b) => b.totalCents - a.totalCents);
  return { totalCents, count, vendors: vendorsOut };
}

/**
 * Credit-card spend in the current calendar month, grouped by vendor. These
 * invoices are "paid" (card vendors), but the card billing cycle is long, so
 * the Bills-due screen surfaces them separately. Confirmed duplicates excluded.
 * @param {string} [today]
 * @returns {Promise<{ totalCents:number, vendors:Array<{vendorId:string,vendorName:string,totalCents:number,count:number}> }>}
 */
export async function cardSpendThisMonth(today = isoToday()) {
  const userId = await requireUserId();
  const monthStart = today.slice(0, 8) + '01';
  const invoices = (await db.invoices.where('userId').equals(userId).toArray())
    .filter((i) => !i.isDuplicate && (i.issuedDate || '') >= monthStart && (i.issuedDate || '') <= today);
  const vendorIds = Array.from(new Set(invoices.map((i) => i.vendorId).filter(Boolean)));
  const vendors = vendorIds.length ? await db.vendors.where('vendorId').anyOf(vendorIds).toArray() : [];
  const vmap = new Map(vendors.map((v) => [v.vendorId, v]));
  const cardIds = new Set(vendors.filter((v) => v.paymentMethod === 'card').map((v) => v.vendorId));

  const byVendor = new Map();
  let totalCents = 0;
  for (const inv of invoices) {
    if (!cardIds.has(inv.vendorId)) continue;
    totalCents += inv.totalCents || 0;
    const cur = byVendor.get(inv.vendorId) || {
      vendorId: inv.vendorId, vendorName: vendorLabel(vmap.get(inv.vendorId)?.name), totalCents: 0, count: 0,
    };
    cur.totalCents += inv.totalCents || 0; cur.count++;
    byVendor.set(inv.vendorId, cur);
  }
  const vendorsOut = Array.from(byVendor.values()).sort((a, b) => b.totalCents - a.totalCents);
  return { totalCents, vendors: vendorsOut };
}

/**
 * Manually mark an invoice paid or unpaid (clears the flag when `null`).
 * @param {string} invoiceId
 * @param {'paid'|'unpaid'|null} state
 */
export async function setInvoicePaid(invoiceId, state) {
  const userId = await requireUserId();
  const inv = await db.invoices.get(invoiceId);
  if (!inv || inv.userId !== userId) return { updated: false };
  const patch = state == null
    ? { paidOverride: undefined, paidDate: undefined }
    : { paidOverride: state, paidDate: state === 'paid' ? isoToday() : undefined };
  await db.invoices.update(invoiceId, patch);
  return { updated: true };
}
